<script setup lang="ts">
import { computed, inject, reactive, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { ChevronDownIcon, ChevronsLeftIcon, ChevronsRightIcon } from 'lucide-vue-next'
import Badge from '@/components/ui/Badge.vue'
import { buildNavigation, findActiveGroupKey, isNavTargetActive, type NavGroup } from '@/navigation'
import { useAppStore } from '@/stores/app'
import { useImportErrorsStore } from '@/stores/importErrors'
import type { UserFunctions } from '@/plugins/keycloak'

const props = defineProps<{ collapsed: boolean }>()
const emit = defineEmits<{ navigate: []; 'update:collapsed': [value: boolean] }>()

const { t } = useI18n()
const route = useRoute()
const appStore = useAppStore()
const importErrors = useImportErrorsStore()
const userFunctions = inject<UserFunctions>('userFunctions')

const groups = computed(() =>
  buildNavigation({
    t,
    isAdmin: userFunctions?.isAdmin() ?? false,
    erps: appStore.currentCompany?.erps ?? [],
    blockedImports: importErrors.blockedImports
  })
)

const openGroups = reactive(new Set<string>())

watch(
  () => findActiveGroupKey(groups.value, route),
  (key) => {
    if (key) openGroups.add(key)
  },
  { immediate: true }
)

const isGroupActive = (group: NavGroup) => findActiveGroupKey([group], route) === group.key

const toggleGroup = (group: NavGroup) => {
  if (props.collapsed) {
    openGroups.add(group.key)
    emit('update:collapsed', false)
    return
  }
  if (openGroups.has(group.key)) openGroups.delete(group.key)
  else openGroups.add(group.key)
}

const rowClass = computed(() => [
  'relative flex h-8 w-full items-center gap-2.5 rounded-md text-sm font-medium transition-colors',
  props.collapsed ? 'justify-center px-0' : 'px-2.5'
])
const itemClass = 'relative flex h-8 items-center rounded-md pl-9 pr-2.5 text-sm transition-colors'
const activeClass =
  'bg-primary-50 text-primary-700 dark:bg-primary-400/15 dark:text-primary-300 before:absolute before:inset-y-1.5 before:left-0 before:w-0.5 before:rounded-full before:bg-primary'
const inactiveClass =
  'text-surface-600 hover:bg-surface-100 hover:text-surface-900 dark:text-surface-400 dark:hover:bg-surface-800 dark:hover:text-surface-0'
</script>

<template>
  <nav class="flex h-full flex-col" :aria-label="t('layout.sidebar.label')">
    <div
      class="flex h-12 shrink-0 items-center border-b border-surface-200 dark:border-surface-800"
      :class="collapsed ? 'justify-center px-2' : 'px-4'"
    >
      <img
        src="/logo.png"
        alt="Mobidic"
        :class="collapsed ? 'h-6 w-6 object-cover object-left' : 'h-7 w-auto'"
      />
    </div>

    <ul class="flex-1 space-y-0.5 overflow-y-auto p-2">
      <li v-for="group in groups" :key="group.key">
        <RouterLink
          v-if="group.to"
          v-tooltip.right="collapsed ? group.label : undefined"
          :to="group.to"
          :data-test="`nav-group-${group.key}`"
          :aria-current="isGroupActive(group) ? 'page' : undefined"
          :class="[rowClass, isGroupActive(group) ? activeClass : inactiveClass]"
          @click="emit('navigate')"
        >
          <component :is="group.icon" class="size-4 shrink-0" aria-hidden="true" />
          <span v-if="!collapsed" class="truncate">{{ group.label }}</span>
        </RouterLink>

        <template v-else>
          <button
            v-tooltip.right="collapsed ? group.label : undefined"
            type="button"
            :data-test="`nav-group-${group.key}`"
            :aria-expanded="!collapsed && openGroups.has(group.key)"
            :aria-label="collapsed ? group.label : undefined"
            :class="[rowClass, collapsed && isGroupActive(group) ? activeClass : inactiveClass]"
            @click="toggleGroup(group)"
          >
            <component :is="group.icon" class="size-4 shrink-0" aria-hidden="true" />
            <template v-if="!collapsed">
              <span class="flex-1 truncate text-left">{{ group.label }}</span>
              <Badge
                v-if="group.badge"
                v-tooltip.top="t('layout.menu.import.blockedTooltip')"
                :value="group.badge"
                severity="danger"
                data-test="nav-badge"
              />
              <ChevronDownIcon
                class="size-4 shrink-0 transition-transform"
                :class="openGroups.has(group.key) ? '' : '-rotate-90'"
                aria-hidden="true"
              />
            </template>
            <span
              v-else-if="group.badge"
              class="absolute right-1.5 top-1.5 size-2 rounded-full bg-red-600"
              data-test="nav-badge-dot"
            />
          </button>

          <ul v-if="!collapsed && openGroups.has(group.key)" class="mt-0.5 space-y-0.5">
            <li v-for="item in group.items" :key="item.key">
              <RouterLink
                :to="item.to"
                :data-test="`nav-item-${item.key}`"
                :aria-current="isNavTargetActive(item, route) ? 'page' : undefined"
                :class="[itemClass, isNavTargetActive(item, route) ? activeClass : inactiveClass]"
                @click="emit('navigate')"
              >
                <span class="truncate">{{ item.label }}</span>
              </RouterLink>
            </li>
          </ul>
        </template>
      </li>
    </ul>

    <div class="hidden shrink-0 border-t border-surface-200 p-2 lg:block dark:border-surface-800">
      <button
        type="button"
        data-test="sidebar-collapse"
        :aria-label="collapsed ? t('layout.sidebar.expand') : t('layout.sidebar.collapse')"
        :class="[rowClass, inactiveClass]"
        @click="emit('update:collapsed', !collapsed)"
      >
        <ChevronsRightIcon v-if="collapsed" class="size-4" aria-hidden="true" />
        <ChevronsLeftIcon v-else class="size-4" aria-hidden="true" />
        <span v-if="!collapsed">{{ t('layout.sidebar.collapse') }}</span>
      </button>
    </div>
  </nav>
</template>
